import React, { useCallback, useContext } from 'react';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { SalvNumberField, SalvRadioToggleButton, SalvRadioToggleGroup, Size } from '@salvapp/ui-components';
import { getMatchIcon, getMatchLabel } from '../../configure/new/helper';
import './MatchingTypeStep.scss';
import { InternalApiScreeningMatchType } from '../../../api-internal';
import ExternalLink from '../../../common/ExternalLink';
import { useMe } from '../../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';

export const MatchingTypeStep = () => {
  const { matchType, updateMatchingType } = useContext(ScreenableFieldSetupContext);
  const { features } = useMe();
  const isContainsWithScoreEnabled = isFeatureEnabled(ORGANISATION_FEATURE.CONTAINS_WITH_SCORE, features);

  const updateType = useCallback(
    (type: string) => {
      updateMatchingType({
        type: type as InternalApiScreeningMatchType,
        value: getDefaultValue(type as InternalApiScreeningMatchType),
      });
    },
    [matchType, updateMatchingType]
  );

  const getDefaultValue = (type: InternalApiScreeningMatchType) => {
    if (isContainsWithScoreEnabled) {
      switch (type) {
        case InternalApiScreeningMatchType.FUZZY_MATCH:
        case InternalApiScreeningMatchType.FUZZY_CONTAINS:
        case InternalApiScreeningMatchType.CONTAINS:
          return 95;
        case InternalApiScreeningMatchType.STARTS_WITH:
          return 8;
        case InternalApiScreeningMatchType.EXACT_MATCH:
        default:
          return undefined;
      }
    }
    switch (type) {
      case InternalApiScreeningMatchType.FUZZY_MATCH:
      case InternalApiScreeningMatchType.FUZZY_CONTAINS:
        return 95;
      case InternalApiScreeningMatchType.STARTS_WITH:
        return 8;
      case InternalApiScreeningMatchType.CONTAINS:
      case InternalApiScreeningMatchType.EXACT_MATCH:
      default:
        return undefined;
    }
  };

  const displayAdditionalField = useCallback(() => {
    if (isContainsWithScoreEnabled) {
      switch (matchType.type) {
        case InternalApiScreeningMatchType.FUZZY_MATCH:
        case InternalApiScreeningMatchType.FUZZY_CONTAINS:
        case InternalApiScreeningMatchType.CONTAINS:
          return displayScoreThreshold();
        case InternalApiScreeningMatchType.STARTS_WITH:
          return displayPrefixLimit();
        case InternalApiScreeningMatchType.EXACT_MATCH:
        default:
          return <></>;
      }
    }
    switch (matchType.type) {
      case InternalApiScreeningMatchType.FUZZY_MATCH:
      case InternalApiScreeningMatchType.FUZZY_CONTAINS:
        return displayScoreThreshold();
      case InternalApiScreeningMatchType.STARTS_WITH:
        return displayPrefixLimit();
      case InternalApiScreeningMatchType.CONTAINS:
      case InternalApiScreeningMatchType.EXACT_MATCH:
      default:
        return <></>;
    }
  }, [matchType]);

  const displayScoreThreshold = useCallback(() => {
    return (
      <SalvNumberField
        label='Fuzzy matching threshold'
        description='The higher the threshold, the more accurate the matches are. The recommended value for the threshold is 95.'
        value={matchType.value || 95}
        minValue={90}
        maxValue={100}
        size={Size.SMALL}
        onChange={(value) =>
          updateMatchingType({
            type: matchType.type,
            value,
          })
        }
      />
    );
  }, [matchType]);

  const displayPrefixLimit = useCallback(() => {
    return (
      <SalvNumberField
        label='Prefix length'
        value={matchType.value ? matchType.value : 8}
        minValue={1}
        size={Size.SMALL}
        onChange={(value) =>
          updateMatchingType({
            type: InternalApiScreeningMatchType.STARTS_WITH,
            value,
          })
        }
      />
    );
  }, [matchType]);

  const MatchTypeExplanation = () => {
    switch (matchType.type) {
      case InternalApiScreeningMatchType.STARTS_WITH:
        return (
          <div className='text-s chapter-subtitle m-t-8'>
            <div className={'m-b-4'}>
              {"When screening against Dow Jones Lists only the Dow Jones records' bank BIC is checked. " +
                'Alerts will be generated if the screened field is also a bank BIC.'}
            </div>
            <div>This restriction does not apply for Custom Lists.</div>
          </div>
        );
      case InternalApiScreeningMatchType.EXACT_MATCH:
        return (
          <div className='text-s chapter-subtitle m-t-8'>
            <div className={'m-b-4'}>
              {"When screening against Dow Jones Lists only the Dow Jones records' bank name is checked. " +
                'Alerts will be generated if the screened field is also a bank name.'}
            </div>
            <div>This restriction does not apply for Custom Lists.</div>
          </div>
        );

      case InternalApiScreeningMatchType.FUZZY_MATCH:
      case InternalApiScreeningMatchType.FUZZY_CONTAINS:
      case InternalApiScreeningMatchType.CONTAINS:
        return undefined;
    }
  };

  return (
    <div className='p-a-24'>
      <div className='matching-type-step'>
        <h4 className='text-strong-m chapter-title'>Matching type</h4>
        <span className='text-s chapter-subtitle'>
          There are multiple ways your data can be matched with list records. Matching behaviour may vary based on
          selected list types. Learn more about Screening matching types{' '}
          <ExternalLink title={'here'} link={'https://help.salv.com/en/articles/233957-matching-types'} />.
        </span>
        <div className='m-t-24'>
          <SalvRadioToggleGroup
            className='match-type-toggle'
            value={matchType.type}
            ariaLabel='Matching type'
            onChange={updateType}
          >
            {Object.values(InternalApiScreeningMatchType).map((type) => (
              <SalvRadioToggleButton key={type} label={getMatchLabel(type)} value={type} icon={getMatchIcon(type)} />
            ))}
          </SalvRadioToggleGroup>
        </div>
        <MatchTypeExplanation />
        <div className='m-t-32 w-50'>{displayAdditionalField()}</div>
      </div>
    </div>
  );
};
